/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import request from 'core/fetchRequest';
import ApiError from 'core/error/ApiError';
import NetworkError from 'core/error/NetworkError';

/**
 * @typedef {Object} AttachmentsUploadData
 * @property {String} uploadMethod - upload the file with `fetch(uploadUrl, {method: uploadMethod, body: file})`
 * @property {String}  uploadUrl - upload the file with `fetch(uploadUrl, {method: uploadMethod, body: file})`
 * @property {String}  downloadUrl - once you upload the file, it will be accessible by this url
 * @property {String}  id - identifier of this file that should be inluded in response, will be used by the server
 * @property {String} uploadServiceType - returns 'default' for this proxy; used to configure upload process if it depends on proxy/configuration
 */
/**
 * Get info needed to upload a file that will be included somewhere in item response.
 * This info is different for each file and needs to be generated by the server.
 * After you get this info, you need to do the actual upload in another request.
 * Used to add images in ExtendedText interaction.
 * @param {Object} config - proxy config
 * @param {String} itemIdentifier
 * @param {String} responseIdentifier
 * @param {Object} [options]
 * @returns {Promise<AttachmentsUploadData>}
 */
export function getAttachmentsUploadData(config, itemIdentifier, responseIdentifier, options = {}) {
    if (!config.attachmentsUploadDataUrl) {
        throw new Error('Get attachments upload data: url is not configured');
    }
    if (!responseIdentifier || !itemIdentifier) {
        throw new Error('Get attachments upload data: missing parameters');
    }

    const url = new URL(config.attachmentsUploadDataUrl);
    Object.entries(options.searchParams || {}).forEach(([key, value]) => {
        url.searchParams.append(key, value.toString());
    });

    const form = new FormData();
    form.append('item_id', itemIdentifier);
    form.append('response_id', responseIdentifier);

    const requestOptions = {
        jwtTokenHandler: config.jwtTokenHandler,
        method: 'POST',
        body: form,
        signal: options.signal
    };
    if (typeof config.requestTimeout === 'number') {
        requestOptions.timeout = config.requestTimeout;
    }
    return request(url.toString(), requestOptions).then(response => {
        if (
            response &&
            response.data &&
            response.data.uploadMethod &&
            response.data.uploadUrl &&
            response.data.downloadUrl &&
            response.data.id
        ) {
            return {
                uploadMethod: response.data.uploadMethod,
                uploadUrl: response.data.uploadUrl,
                downloadUrl: response.data.downloadUrl,
                id: response.data.id,
                uploadServiceType: 'default'
            };
        }
        throw new Error('Get attachments upload data: unexpected response format');
    });
}

/**
 * Make a network request (whether it is a structured 'action' or not),
 * transform its response (as an action) and handle any errors
 * @param {string} url
 * @param {object} requestOptions
 * @param {object} [handlingOptions]
 * @param {Boolean} [handlingOptions.returnRawResponse] - causes early return of raw response
 * @param {Boolean} [handlingOptions.returnParsedJsonResponse] - causes early return of parsed JSON
 * @returns {Promise<Object>} data, by default post-transformation of action response
 */
export const doRequest = async (url, requestOptions = {}, handlingOptions = {}) => {
    let rawResponse;
    try {
        requestOptions = Object.assign({}, requestOptions, { returnOriginalResponse: true });
        rawResponse = await request(url, requestOptions);
    } catch (err) {
        if (err && err instanceof NetworkError && err.errorCode > 0 && err.response) {
            //try to get errorCode/errorMessage returned by server to throw more detailed error
            try {
                const requestResponse = await err.response.clone().json(); //clone because can't `.json()` on the same stream twice
                if (requestResponse.success === true) {
                    //server can send response with `500` status, `success:true,errorCode:null` on root level, and actual error inside `responses` only
                    rawResponse = err.response;
                } else if (requestResponse.errorCode) {
                    throw new ApiError(
                        `${requestResponse.errorCode}: ${requestResponse.errorMessage}`,
                        requestResponse.errorCode,
                        err.response
                    );
                } else {
                    throw err;
                }
            } catch (innerErr) {
                if (innerErr instanceof ApiError) {
                    throw innerErr;
                }
                throw err;
            }
        } else {
            const isGenericFetchError =
                err instanceof TypeError &&
                err.message &&
                (err.message.includes('Failed to fetch') || err.message.includes('Load failed'));
            if (isGenericFetchError) {
                err.recoverable = true;
            }
        }
        throw err;
    }

    if (!rawResponse) {
        throw new Error(`Action error: null or empty response`);
    }

    if (handlingOptions.returnRawResponse) {
        return rawResponse;
    }

    // process response...

    let requestResponse;
    try {
        requestResponse = await rawResponse.json();
    } catch (err) {
        if (err) {
            //should it be recoverable or not?..
            err.isResponseToJsonError = true;
            //let's assume it's recoverable, and caller can check `isResponseToJsonError` to override this behavior.
            err.recoverable = true;
        }
        throw err;
    }

    if (handlingOptions.returnParsedJsonResponse) {
        return requestResponse;
    }

    // destructure & check response data...

    const responses = requestResponse?.responses;
    if (!responses) {
        throw new Error('Action error: "responses" field in response is empty');
    }

    const response = responses[0];
    const unsuccessful = response.filter(result => result.success === false);
    if (unsuccessful.length > 0) {
        const errorMessages = unsuccessful.map(r => `${r.errorCode || 0}: ${r.errorMessage || ''}`);
        const error = new Error(`Action error: ${errorMessages.join('; ')}`);
        error.response = requestResponse;
        if (unsuccessful.some(r => r.errorCode === 409)) {
            error.errorCode = 409;
        }
        throw error;
    }

    return response.map(result => result.values);
};

/**
 * Generic data fetcher using proxy config & passed configuration
 * @public
 * @param {Object} config - proxy config
 * @param {string} url
 * @param {object} [requestOptions]
 * @param {object} [handlingOptions]
 * @returns {Promise<*>}
 */
export function getData(config, url, requestOptions = {}, handlingOptions = {}) {
    if (!requestOptions.jwtTokenHandler && config.jwtTokenHandler) {
        requestOptions.jwtTokenHandler = config.jwtTokenHandler;
    }
    if (typeof requestOptions.timeout !== 'number' && typeof config.requestTimeout === 'number') {
        requestOptions.timeout = config.requestTimeout;
    }
    return doRequest(url, requestOptions, handlingOptions);
}
